import "./../../styles.css";
// import { useMemo, useState } from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Row, Col, Card } from "antd";
import {
  EyeOutlined,
  // LoadingOutlined,
  // ExclamationCircleOutlined,
  ExclamationOutlined,
} from "@ant-design/icons";
import { Empty, Table, Tag, Tooltip, Button, Modal } from "antd";
import { createTestRideAPI, getVehicleTrips } from "apis/vehicleAnalyticsApis";
import { formatDateAndTime, formatHourAndMins } from "components/Common/Utils";
import automaticTripSVG from "assets/images/automatic_trip.svg";
import testTripSVG from "assets/images/test_trip.svg";
import { ROUTES, UNITS } from "components/Common/Constants";
import { VEHICLE_ANALYTICS } from "components/Common/svgIconComponents";
import AutomaticTripIcon from "./Icons/AutomaticTrip";
import TestTripIcon from "./Icons/TestTrip";
import {
  TestRideSubmissionInfoPopup,
  TestRideSubmissionPopup,
  TestRideSubmissionReTryPopup,
} from "components/Common/confirmationPopup";
import InfoCollapse from "components/Common/InfoBoxes/InfoCollapse";
import {
  setSelectedTab,
  setTestId,
  // setTestRideStatus,
  setTripSummary,
} from "reducers/testRideSlice";
import TripDetailsModal from "./TripDetailsModal";
import TripDetailsCard from "./TripDetailsCard";
import Loading from "components/Common/Loading";
import APIError from "components/Common/APIError";
import { TestRideFaildIcon } from "./Icons/icons";

const latestTripTitle = (
  <div className="vaCardTitle">
    <VEHICLE_ANALYTICS.TRIPS />
    <span>Latest Trip</span>
  </div>
);
const tripsHistoryTitle = (
  <div className="vaCardTitle">
    <VEHICLE_ANALYTICS.TRIPS />
    <span>Trips History</span>
  </div>
);

const Trips = ({ selectedDateRange, datePickerType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const eol = useSelector((state) => state.eol);
  const vehicleDetails = eol?.vehicleDetails;
  // const testRideStatus = useSelector((state) => state.testRide?.testRideStatus);
  const [modal, contextHolder] = Modal.useModal();
  const [submittedTrip, setSubmittedTrip] = useState({});
  const [tripDetailsModal, setTripDetailsModal] = useState({ open: false });
  // const [latestTripDetails, setLatestTripDetails] = useState({});
  const [paginationDetails, setPaginationDetails] = useState({
    current: 1,
    pageSize: 10,
  });

  let filter = "";
  let sortKey = "duration";

  switch (datePickerType) {
    case "day":
      filter = "DAILY";
      break;
    case "week":
      filter = "WEEKLY";
      break;
    case "month":
      filter = "MONTHLY";
      break;
    default:
      break;
  }

  const {
    data: createTestRideData,
    isLoading: createTestRideLoading,
    isError: createTestRideIsError,
    error: createTestRideError,
  } = useQuery({
    queryKey: ["createTestRide", submittedTrip?.id],
    queryFn: () => createTestRideAPI(submittedTrip?.id),
    enabled: !!(vehicleDetails?.vehImei && submittedTrip?.id),
  });

  const {
    data: vehicleTripsData,
    isLoading: vehicleTripsDataLoading,
    isError: tripsDataIsError,
    error: tripsDataError,
  } = useQuery({
    queryKey: [
      "vehicleTrips",
      vehicleDetails?.vehImei,
      selectedDateRange,
      datePickerType,
      createTestRideData,
      paginationDetails,
    ],
    queryFn: () =>
      getVehicleTrips(
        vehicleDetails?.vehImei,
        selectedDateRange,
        filter,
        paginationDetails.current - 1,
        paginationDetails.pageSize,
        sortKey
      ),
    keepPreviousData: true,
    enabled: !!(vehicleDetails?.vehImei && selectedDateRange && datePickerType),
    staleTime: 1 * 10 * 60 * 1000,
  });

  // useMemo(() => {
  //   console.log(vehicleTripsData);
  //   setLatestTripDetails(vehicleTripsData?.latestTrip || {})
  // },[vehicleDetails?.vehImei])

  const viewTestRideDetails = (tripDetails) => {
    if (tripDetails?.testId) {
      dispatch(setTripSummary({}));
      dispatch(setTestId(tripDetails?.testId));
      dispatch(setSelectedTab("rideStatistics"));
      navigate(ROUTES.TEST_RIDES.link, {
        state: { testId: tripDetails?.testId, vehicleDetails },
      });
    } else {
      setTripDetailsModal({ open: true, ...tripDetails });
    }
  };

  const columns = [
    {
      title: "Trip #",
      dataIndex: "id",
      key: "id",
      className: "grey align-text-top",
    },
    {
      title: "Time",
      dataIndex: "time",
      key: "time",
      width: "36%",
      className: "grey",
      render: (_, record) =>
        dayjs(record?.startTime).date() === dayjs(record?.endTime).date() ? (
          <>
            <div className="f12">
              {dayjs(record?.startTime).format("MMM DD YYYY")}
            </div>
            <div className="f12">
              {dayjs(record?.startTime).format("hh:mm A")} to{" "}
              {dayjs(record?.endTime).format("hh:mm A")}
            </div>
          </>
        ) : (
          <>
            <div className="f12">{formatDateAndTime(record?.startTime)}</div>
            <div className="f12">{formatDateAndTime(record?.endTime)}</div>
          </>
        ),
    },
    {
      title: "Distance",
      dataIndex: "distance",
      key: "distance",
      align: "center",
      className: "grey align-text-top",
      render: (distance) => `${distance} ${UNITS.KM}`,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      className: "grey align-text-top",
      render: (duration) => formatHourAndMins(duration),
    },
    {
      title: "Trip Type",
      dataIndex: "tripType",
      key: "tripType",
      className: "grey align-text-top",
      render: (tripType, record) => (
        <div style={{ alignItems: "center" }}>
          <>
            <Tag
              color={
                ["FAILED", "ABORTED"].includes(record.testStatus)
                  ? "red"
                  : tripType === "AUTOMATIC"
                  ? "blue"
                  : "green"
              }
            >
              {tripType === "AUTOMATIC" ? (
                <AutomaticTripIcon />
              ) : (
                <TestTripIcon style={{ fontSize: 12 }} />
              )}
              <span>{tripType}</span>
            </Tag>
          </>
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "tripType",
      key: "tripType",
      className: "grey align-text-top",
      render: (_, record) => (
        <div className="align-center">
          <Tooltip title={"View Details"}>
            <Button
              shape={"circle"}
              onClick={() => viewTestRideDetails(record)}
            >
              <EyeOutlined />
            </Button>
          </Tooltip>
          {record.testId === submittedTrip?.id &&
          (["FAILED", "ABORTED"].includes(record.testStatus) ||
            createTestRideIsError) ? (
            <Tooltip
              title={
                createTestRideIsError
                  ? createTestRideError?.message
                  : "Converation to Test Ride was " +
                    record?.testStatus?.toLowerCase()
              }
            >
              <Button
                shape={"circle"}
                onClick={() => onSubmitTestRide(record)}
                className="ml05"
              >
                {/* <ExclamationOutlined
                  className=""
                  style={{ fontSize: "16px" }}
                /> */}
                <TestRideFaildIcon />
              </Button>
              {/* <WarningOutlined
                className="red ml05 pl05"
                style={{ fontSize: "24px" }}
              /> */}
            </Tooltip>
          ) : record.tripType === "AUTOMATIC" ? (
            <Tooltip title={"Submit trip as Test Ride"}>
              <Button
                shape={"circle"}
                onClick={() => onSubmitTestRide(record)}
                loading={submittedTrip?.id === record.id}
                className="ml05"
              >
                {submittedTrip?.id !== record.id ? <TestTripIcon /> : null}
              </Button>
            </Tooltip>
          ) : record.testId === submittedTrip?.id && createTestRideLoading ? (
            <Tooltip title={"Processing"}>
              <Button
                shape={"circle"}
                loading={submittedTrip?.id === record.id}
                className="ml05"
              />
            </Tooltip>
          ) : null}
        </div>
      ),
    },
  ];

  const onSubmitTestRide = async (tripDetails) => {
    if (createTestRideLoading) {
      modal.info(TestRideSubmissionInfoPopup);
    } else {
      const confirmation = await modal.confirm(
        tripDetails?.testId
          ? TestRideSubmissionReTryPopup
          : TestRideSubmissionPopup
      );
      if (confirmation && tripDetails.id !== submittedTrip?.id) {
        setSubmittedTrip(tripDetails);
      }
    }
  };

  const renderInfoDesction = () => {
    return (
      <p>
        A <span style={{ fontWeight: 600 }}>'Trip Start'</span> is detected if a
        vehicle has been in running state for more than 1 minute. The{" "}
        <span style={{ fontWeight: 600 }}>'Trip End'</span> is detected if the
        vehicle is in stopped state for at least 10 minutes.
      </p>
    );
  };

  const closeTripDetailsModal = () => {
    setTripDetailsModal({ open: false });
  };

  const handlePageChange = (pagination) => {
    setPaginationDetails({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  return (
    <div>
      {contextHolder}
      <Modal
        title={""}
        centered
        open={tripDetailsModal?.open}
        onCancel={closeTripDetailsModal}
        width={{
          xs: "90%",
          sm: "80%",
          md: "75%",
          lg: "80%",
          xl: "90%",
          xxl: "90%",
        }}
        footer={null}
      >
        <TripDetailsModal tripDetailsData={tripDetailsModal} />
      </Modal>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={24} lg={8} xxl={6}>
          <Row gutter={[16, 16]} className="h100">
            <Col xs={24} md={14} lg={24}>
              <Card
                title={"Automatic Trips and Test Rides"}
                className="vaCards"
                style={{ height: "100%" }}
              >
                <Row style={{ padding: "10px" }}>
                  <Col span={24}>
                    <Row>
                      <Col span={7}>
                        <img
                          alt="trip"
                          src={automaticTripSVG}
                          width={75}
                          height={75}
                        />
                      </Col>
                      <Col span={17}>
                        <span style={{ fontWeight: 800, lineHeight: "12px" }}>
                          Automatic Trips
                        </span>
                        <p>
                          {" "}
                          We automatically detect the trips taken by a vehicle
                          and generate basic analysis.
                        </p>
                      </Col>
                      <Col span={7}>
                        <img
                          alt="trip"
                          src={testTripSVG}
                          width={75}
                          height={75}
                        />
                      </Col>
                      <Col span={17}>
                        <span style={{ fontWeight: 800 }}>Test Rides</span>
                        <p>
                          {" "}
                          Some of the automatic trips can be marked as 'Test
                          Rides' for more detailed analysis.
                        </p>
                      </Col>
                      <Col span={24}>
                        <InfoCollapse
                          title={"Trip Detection"}
                          description={renderInfoDesction()}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={10} lg={24} className="latest-trip-col">
              <TripDetailsCard
                tripCardTitle={latestTripTitle}
                tripDetails={vehicleTripsData?.latestTrip}
              />
            </Col>
          </Row>
        </Col>
        <Col xs={24} md={24} lg={16} xxl={18}>
          <Card
            bordered={false}
            title={tripsHistoryTitle}
            className="h100 trips-table-card"
          >
            {vehicleTripsDataLoading ? (
              <Loading />
            ) : vehicleTripsData?.data?.length > 0 ? (
              <Table
                data-testid="ride-histories"
                columns={columns}
                loading={vehicleTripsDataLoading}
                dataSource={vehicleTripsData?.data}
                rowKey={(record) => record.testId}
                scroll={{ x: "100%" }}
                className="mb05"
                onChange={handlePageChange}
                pagination={{
                  current: paginationDetails.current,
                  pageSize: paginationDetails.pageSize,
                  total: vehicleTripsData.totalElements,
                  showSizeChanger: false,
                  showQuickJumper: false,
                  hideOnSinglePage: true,
                }}
              />
            ) : tripsDataIsError ? (
              <APIError message={tripsDataError.message} />
            ) : (
              <div className="pd3025" data-testid="empty-ride-histories">
                <Empty />
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Trips;
